import axios from 'axios';
import type {
  CustomField,
  CustomFieldsByLayer,
  CustomFieldValue,
  PersonCustomFieldApiResponse,
  SaveRequest,
  GetCustomFieldsByItemIdRequest,
  GetEmptyCustomFieldsByConfigValueIdRequest,
  GetListComboBoxRequest,
  GetImageRequest,
  GetProjectCustomFieldsByItemIdRequest,
  GetLayersByConfigValueIdRequest,
} from '@/components/CustomFields/types';
import * as APIUtils from '../components/Globals/Utils';

class CustomFieldsService
{
  /**
   * Get custom fields for an item
   */
  async getCustomFields(
    apiUrl: string,
    token: string,
    projectId: number,
    configValueId: number,
    coreTaskId?: number,
    isDeleted: boolean = false,
  ): Promise<CustomFieldsByLayer[]>
  {
    const response = await axios.get(`${apiUrl}/api/CustomFields/GetByItem`, {
      params: {
        projectId,
        configValueId,
        coreTaskId,
        isDeleted,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * Get custom fields for a person
   * Returns an array of layers, each containing custom fields
   */
  async getPersonCustomFieldsByItemId(
    apiUrl: string,
    token: string,
    personId: number,
    isDeleted: boolean = false,
  ): Promise<PersonCustomFieldApiResponse[]>
  {
    const url = `${apiUrl}/api/CustomField/GetPersonCustomFieldsByItemId`;
    const params = {
      PersonId: personId,
      IsDeleted: isDeleted,
    };

    try
    {
      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    }
    catch (error)
    {
      console.error('❌ CustomFieldsService - Request failed:', error);
      if (axios.isAxiosError(error))
      {
        console.error('❌ Axios error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });
      }
      throw error;
    }
  }

  /**
   * Get empty custom fields template for a config value
   */
  async getEmptyCustomFields(
    apiUrl: string,
    token: string,
    configValueId: number,
  ): Promise<CustomFieldsByLayer[]>
  {
    const response = await axios.get(`${apiUrl}/api/CustomFields/GetEmpty/${configValueId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * Save custom field values
   */
  async saveCustomFieldValues(
    apiUrl: string,
    token: string,
    coreTaskId: number,
    values: CustomFieldValue[],
  ): Promise<void>
  {
    await axios.post(
      `${apiUrl}/api/CustomFields/SaveValues`,
      {
        CoreTaskId: coreTaskId,
        Values: values,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  }

  /**
   * Update single custom field value
   */
  async updateCustomFieldValue(
    apiUrl: string,
    token: string,
    customFieldId: number,
    coreTaskId: number,
    value: string | number | string[] | null,
  ): Promise<void>
  {
    await axios.put(
      `${apiUrl}/api/CustomFields/UpdateValue`,
      {
        CustomFieldId: customFieldId,
        CoreTaskId: coreTaskId,
        Value: value,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  }

  /**
   * Validate custom field value
   */
  validateField(field: CustomField, value: any): string | null
  {
    // Required validation
    if (field.IsRequired && !value)
    {
      return `${field.FieldName} is required`;
    }

    // Type-specific validation
    switch (field.FieldType)
    {
      case 'number':
      case 'decimal':
        if (value !== null && value !== '')
        {
          const numValue = Number(value);
          if (isNaN(numValue))
          {
            return `${field.FieldName} must be a valid number`;
          }
          if (field.MinValue !== undefined && numValue < field.MinValue)
          {
            return `${field.FieldName} must be at least ${field.MinValue}`;
          }
          if (field.MaxValue !== undefined && numValue > field.MaxValue)
          {
            return `${field.FieldName} must be at most ${field.MaxValue}`;
          }
        }
        break;

      case 'text':
      case 'multitext':
        if (value && field.ValidationRegex)
        {
          const regex = new RegExp(field.ValidationRegex);
          if (!regex.test(value.toString()))
          {
            return `${field.FieldName} format is invalid`;
          }
        }
        break;

      case 'hyperlink':
        if (value)
        {
          try
          {
            new URL(value.toString());
          }
          catch
          {
            return `${field.FieldName} must be a valid URL`;
          }
        }
        break;
    }

    return null;
  }

  /**
   * Validate all custom fields
   */
  validateAllFields(fields: CustomField[]): Map<number, string>
  {
    const errors = new Map<number, string>();

    fields.forEach(field =>
    {
      const error = this.validateField(field, field.Value);
      if (error)
      {
        errors.set(field.CustomFieldId, error);
      }
    });

    return errors;
  }

  /**
   * Get custom field options
   */
  async getFieldOptions(apiUrl: string, token: string, customFieldId: number): Promise<any[]>
  {
    const response = await axios.get(`${apiUrl}/api/CustomFields/${customFieldId}/Options`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }

  /**
   * Format custom field value for display
   */
  formatDisplayValue(field: CustomField): string
  {
    if (!field.Value) return '';

    switch (field.FieldType)
    {
      case 'date':
        return new Date(field.Value as string).toLocaleDateString();

      case 'decimal':
        return Number(field.Value).toFixed(2);

      case 'multiselect':
        if (Array.isArray(field.Value))
        {
          const selectedOptions = field.Options?.filter(opt => field.Value!.includes(opt.Value));
          return selectedOptions?.map(opt => opt.Label).join(', ') || '';
        }
        return '';

      case 'combobox':
        const option = field.Options?.find(opt => opt.Value === field.Value);
        return option?.Label || field.Value.toString();

      default:
        return field.Value.toString();
    }
  }

  save<T = unknown>(requestConfig: SaveRequest): Promise<T>
  {
    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      'api/customfieldvalue/',
      'PUT',
      requestConfig.request,
      true,
    );
  }

  savePost<T = unknown>(requestConfig: SaveRequest): Promise<T>
  {
    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      'api/customfieldvalue/',
      'POST',
      requestConfig.request,
    );
  }

  getCustomFieldsByItemId<T = unknown>(requestConfig: GetCustomFieldsByItemIdRequest): Promise<T>
  {
    const params = new URLSearchParams({
      CoreTaskId: String(requestConfig.coreTaskId),
      IsDeleted: String(requestConfig.isDeleted),
      ProjectId: String(requestConfig.projectId),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      `api/CustomField/GetCustomFieldsByItemId?${params.toString()}`,
    );
  }

  getEmptyCustomFieldsByConfigValueId<T = unknown>(
    requestConfig: GetEmptyCustomFieldsByConfigValueIdRequest,
  ): Promise<T>
  {
    const params = new URLSearchParams();
    if (requestConfig.configValueId != null)
      params.append('configValueId', String(requestConfig.configValueId));
    if (requestConfig.projectId != null)
      params.append('ProjectId', String(requestConfig.projectId));

    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      `api/configvalue/GetEmptyCustomFieldsByConfigValueId?${params.toString()}`,
    );
  }

  getListComboBox<T = unknown>(requestConfig: GetListComboBoxRequest): Promise<T>
  {
    return APIUtils.callFetch<T>(requestConfig.api as any, requestConfig.serviceUrl);
  }

  getImageResult<T = unknown>(requestConfig: GetImageRequest): Promise<T>
  {
    const params = new URLSearchParams({
      customImageId: String(requestConfig.request),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      `api/customfieldvalue/GetImageResult?${params.toString()}`,
    );
  }

  getThumbnail<T = unknown>(requestConfig: GetImageRequest): Promise<T>
  {
    const params = new URLSearchParams({
      customValue: String(requestConfig.request),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      `api/customfieldvalue/GetThumbnail?${params.toString()}`,
    );
  }

  uploadImage<T = unknown>(requestConfig: SaveRequest): Promise<T>
  {
    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      'api/uploadimage/',
      'POST',
      requestConfig.request,
    );
  }

  // getPersonCustomFieldsByItemId<T = unknown>(
  //   requestConfig: GetPersonCustomFieldsByItemIdRequest
  // ): Promise<T> {
  //   const params = new URLSearchParams({
  //     PersonId: String(requestConfig.personId),
  //     IsDeleted: String(requestConfig.isDeleted),
  //   });

  //   return APIUtils.callFetch<T>(
  //     requestConfig.api,
  //     `api/CustomField/GetPersonCustomFieldsByItemId?${params.toString()}`
  //   );
  // }

  getProjectCustomFieldsByItemId<T = unknown>(
    requestConfig: GetProjectCustomFieldsByItemIdRequest,
  ): Promise<T>
  {
    const params = new URLSearchParams({
      ProjectId: String(requestConfig.projectId),
      IsDeleted: String(requestConfig.isDeleted),
      IsProject: 'true',
      GetGroupsOneCall: 'false',
    });

    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      `api/CustomField/GetProjectCustomFieldsByItemId?${params.toString()}`,
    );
  }

  getLayersByConfigValueId<T = unknown>(
    requestConfig: GetLayersByConfigValueIdRequest,
  ): Promise<T>
  {
    const params = new URLSearchParams({
      ProjectId: String(requestConfig.projectId),
      ConfigValueId: String(requestConfig.configValueId),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api as any,
      `api/layer/GetLayersByConfigValueId?${params.toString()}`,
    );
  }
}

export default new CustomFieldsService();
